import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar as CalendarIcon,
  Video,
  Video as VideoIcon,
  Phone,
  Plus,
  Users,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Trash2,
  Copy,
  Bell,
  Clock,
  X,
  Circle,
  StopCircle,
  Download,
  Shield,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { toast } from 'sonner';

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const formatDate = (iso) => new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

function EmployeePicker({ options, selected, onChange }) {
  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);
  };
  return (
    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1" data-testid="attendee-picker">
      {options.length === 0 ? (
        <p className="text-xs text-slate-400">No teammates to invite yet.</p>
      ) : (
        options.map((emp) => (
          <label key={emp.user_id} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={selected.includes(emp.user_id)}
              onChange={() => toggle(emp.user_id)}
              className="rounded"
            />
            <span className="flex-1">{emp.name}</span>
            <Badge variant="outline" className="text-[10px]">{emp.role_label}</Badge>
          </label>
        ))
      )}
    </div>
  );
}

function MeetingCard({ meeting, isHost, onJoin, onJoinBrowser, onCopy, onCancel, onStartRec, onStopRec, onDownloadRec, isRecording, uploadingRec }) {
  const soon = new Date(meeting.scheduled_at).getTime() - Date.now() < 15 * 60 * 1000;
  const recordings = meeting.recordings || [];
  return (
    <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-shadow" data-testid={`meeting-card-${meeting.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {meeting.call_type === 'audio' ? (
                <Phone className="w-4 h-4 text-emerald-500 shrink-0" />
              ) : (
                <Video className="w-4 h-4 text-emerald-500 shrink-0" />
              )}
              <h3 className="font-semibold text-slate-800 truncate">{meeting.title}</h3>
              {soon && <Badge className="bg-amber-500 text-white text-xs shrink-0">Starting soon</Badge>}
              {meeting.waiting_room && <Badge variant="outline" className="text-[10px]">Waiting room</Badge>}
              {meeting.mute_on_join && <Badge variant="outline" className="text-[10px]">Muted on join</Badge>}
              {meeting.password && <Badge variant="outline" className="text-[10px]">🔒 Password</Badge>}
              {meeting.auto_record && <Badge variant="outline" className="text-[10px]">Auto-record</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-1">
              <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" /> {formatDate(meeting.scheduled_at)}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatTime(meeting.scheduled_at)} · {meeting.duration_min}m</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {(meeting.attendees?.length || 0) + 1}</span>
            </div>
            {meeting.description && <p className="text-sm text-slate-600 line-clamp-2">{meeting.description}</p>}
            <p className="text-[11px] text-slate-400 mt-1">Host: {meeting.host_name}</p>

            {recordings.length > 0 && (
              <div className="mt-3 space-y-1 border-t pt-2" data-testid={`recordings-${meeting.id}`}>
                <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Recordings ({recordings.length})</p>
                {recordings.map((rec) => (
                  <div key={rec.id} className="flex items-center gap-2 text-xs">
                    <VideoIcon className="w-3 h-3 text-slate-500" />
                    <span className="flex-1 truncate">{new Date(rec.created_at).toLocaleString()} · {Math.round((rec.size_bytes || 0) / (1024 * 1024))}MB · by {rec.recorded_by_name}</span>
                    <Button variant="ghost" size="sm" onClick={() => onDownloadRec(meeting, rec)} data-testid={`download-recording-${rec.id}`}>
                      <Download className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => onJoin(meeting)} data-testid={`join-meeting-${meeting.id}`}>
              <Video className="w-4 h-4 mr-1" /> Join
            </Button>
            {onJoinBrowser && (
              <Button size="sm" variant="ghost" onClick={() => onJoinBrowser(meeting)} data-testid={`join-meeting-browser-${meeting.id}`} title="Open in a new browser tab">
                <ExternalLink className="w-4 h-4 mr-1" /> Browser
              </Button>
            )}
            {isRecording ? (
              <Button size="sm" variant="outline" className="border-red-400 text-red-600 animate-pulse" onClick={onStopRec} data-testid={`stop-recording-${meeting.id}`}>
                <StopCircle className="w-4 h-4 mr-1" /> Stop rec
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => onStartRec(meeting)} disabled={uploadingRec} data-testid={`start-recording-${meeting.id}`}>
                <Circle className="w-4 h-4 mr-1 text-red-500 fill-red-500" /> Record
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onCopy(meeting)} data-testid={`copy-meeting-${meeting.id}`}>
              <Copy className="w-4 h-4 mr-1" /> Link
            </Button>
            {isHost && (
              <Button size="sm" variant="ghost" onClick={() => onCancel(meeting)} data-testid={`cancel-meeting-${meeting.id}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TeamMeetingsPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('agenda'); // agenda | calendar
  const [monthAnchor, setMonthAnchor] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration_min: 30,
    call_type: 'video',
    attendee_ids: [],
  });
  const [creating, setCreating] = useState(false);
  const [recordingMeetingId, setRecordingMeetingId] = useState(null);
  const [recorder, setRecorder] = useState(null);
  const [uploadingRec, setUploadingRec] = useState(false);

  const isEmployee = !!(user?.is_admin || user?.is_staff || user?.intern_role);
  const { subscribe } = usePushSubscription(api, isEmployee);

  const fetchAll = useCallback(async () => {
    try {
      const [meetingsRes, empRes] = await Promise.all([
        api.get('/meetings?range_days=90&include_past=true'),
        api.get('/chat/employees').catch(() => ({ data: { employees: [] } })),
      ]);
      setMeetings(meetingsRes.data.meetings || []);
      setEmployees(empRes.data.employees || []);
    } catch (err) {
      toast.error('Could not load meetings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openScheduler = () => {
    const now = new Date(selectedDay);
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    setForm({
      title: '',
      description: '',
      date: `${yyyy}-${mm}-${dd}`,
      time: '10:00',
      duration_min: 30,
      call_type: 'video',
      attendee_ids: [],
      waiting_room: false,
      mute_on_join: false,
      video_off_on_join: false,
      auto_record: false,
      password: '',
    });
    setDialogOpen(true);
  };

  const buildMeetUrl = (meeting) => {
    // Layer Zoom-like settings on the Jitsi URL via config fragment hash.
    const params = [];
    if (meeting.mute_on_join) params.push('config.startWithAudioMuted=true');
    if (meeting.video_off_on_join || meeting.call_type === 'audio') params.push('config.startWithVideoMuted=true');
    if (meeting.waiting_room) params.push('config.enableLobbyChat=true', 'config.lobby.enabled=true');
    if (meeting.password) params.push(`config.roomPassword="${encodeURIComponent(meeting.password)}"`);
    const hash = params.length ? `#${params.join('&')}` : '';
    return `${meeting.meet_url}${hash}`;
  };

  const createMeeting = async () => {
    if (!form.title.trim()) return toast.error('Give the meeting a title');
    if (!form.date || !form.time) return toast.error('Pick a date and time');
    setCreating(true);
    try {
      const localIso = new Date(`${form.date}T${form.time}:00`).toISOString();
      const res = await api.post('/meetings', {
        title: form.title.trim(),
        description: form.description.trim(),
        scheduled_at: localIso,
        duration_min: Number(form.duration_min) || 30,
        attendee_ids: form.attendee_ids,
        call_type: form.call_type,
        waiting_room: form.waiting_room,
        mute_on_join: form.mute_on_join,
        video_off_on_join: form.video_off_on_join,
        auto_record: form.auto_record,
        password: form.password || null,
      });
      toast.success('Meeting scheduled');
      setDialogOpen(false);
      setMeetings((prev) => [...prev, res.data].sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setCreating(false);
    }
  };

  const joinMeeting = (meeting) => {
    // Stay inside the PWA — MeetingRoom embeds Jitsi with the user pre-filled.
    navigate(`/meeting/${meeting.id}`);
  };

  const joinMeetingInBrowser = (meeting) => {
    window.open(buildMeetUrl(meeting), '_blank', 'noopener');
  };

  const copyLink = (meeting) => {
    navigator.clipboard.writeText(buildMeetUrl(meeting));
    toast.success('Meeting link copied');
  };

  // ============ RECORDING (client-side screen capture) ============
  const startRecording = async (meeting) => {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error('Recording not supported in this browser');
      return;
    }
    try {
      const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      let combinedStream = displayStream;
      // Try to also capture microphone so both sides of the call are recorded
      try {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const audioTracks = [...displayStream.getAudioTracks(), ...micStream.getAudioTracks()];
        combinedStream = new MediaStream([...displayStream.getVideoTracks(), ...audioTracks]);
      } catch (micErr) { void micErr; }

      const chunks = [];
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : 'video/webm';
      const mr = new MediaRecorder(combinedStream, { mimeType });
      mr.ondataavailable = (evt) => { if (evt.data && evt.data.size > 0) chunks.push(evt.data); };
      mr.onstop = async () => {
        combinedStream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: 'video/webm' });
        setUploadingRec(true);
        try {
          const fd = new FormData();
          fd.append('file', blob, `meeting-${meeting.id}.webm`);
          const res = await api.post(`/meetings/${meeting.id}/recording`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000,
          });
          const newRec = res.data.recording;
          setMeetings((prev) => prev.map((m) => m.id === meeting.id
            ? { ...m, recordings: [...(m.recordings || []), newRec] }
            : m));
          toast.success('Recording saved and sent to all attendees');
        } catch (err) {
          toast.error('Recording upload failed');
        } finally {
          setUploadingRec(false);
          setRecorder(null);
          setRecordingMeetingId(null);
        }
      };
      // If the user stops screen-share from browser UI, wrap up cleanly
      displayStream.getVideoTracks()[0].addEventListener('ended', () => {
        if (mr.state !== 'inactive') mr.stop();
      });
      mr.start(1000);
      setRecorder(mr);
      setRecordingMeetingId(meeting.id);
      toast.success('Recording started — pick the Jitsi tab or window to capture');
    } catch (err) {
      if (err?.name !== 'NotAllowedError') toast.error('Could not start recording');
    }
  };

  const stopRecording = () => {
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  };

  const downloadRecording = async (meeting, rec) => {
    try {
      const res = await api.get(`/meetings/${meeting.id}/recording/${rec.filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = rec.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const cancelMeeting = async (meeting) => {
    if (!window.confirm(`Cancel "${meeting.title}"?`)) return;
    try {
      await api.delete(`/meetings/${meeting.id}`);
      setMeetings((prev) => prev.filter((m) => m.id !== meeting.id));
      toast.success('Cancelled');
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const enableNotifications = async () => {
    const res = await subscribe();
    if (res.subscribed) {
      toast.success('Notifications enabled on this device');
      try {
        await api.post('/push/test');
      } catch (err) { void err; }
    } else if (res.reason === 'permission-denied') {
      toast.error('Notification permission blocked. Enable it in your browser settings.');
    } else {
      toast.info(`Could not enable notifications (${res.reason || 'unknown'})`);
    }
  };

  // Calendar helpers
  const daysInMonth = useMemo(() => {
    const start = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
    const end = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 0);
    const leading = start.getDay();
    const days = [];
    for (let i = 0; i < leading; i += 1) days.push(null);
    for (let d = 1; d <= end.getDate(); d += 1) {
      days.push(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), d));
    }
    return days;
  }, [monthAnchor]);

  const meetingsByDay = useMemo(() => {
    const map = new Map();
    meetings.forEach((m) => {
      const d = new Date(m.scheduled_at);
      const key = d.toDateString();
      const arr = map.get(key) || [];
      arr.push(m);
      map.set(key, arr);
    });
    return map;
  }, [meetings]);

  const upcoming = meetings.filter((m) => new Date(m.scheduled_at).getTime() >= Date.now() - 30 * 60 * 1000);
  const past = meetings.filter((m) => new Date(m.scheduled_at).getTime() < Date.now() - 30 * 60 * 1000);
  const dayMeetings = meetingsByDay.get(selectedDay.toDateString()) || [];

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="team-meetings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-fredoka font-bold text-slate-800 flex items-center gap-2">
            <CalendarIcon className="w-7 h-7 text-emerald-500" />
            Team Meetings
          </h1>
          <p className="text-sm text-slate-500">Free video &amp; audio calls via Jitsi Meet. No accounts needed.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-full border overflow-hidden">
            <button
              type="button"
              onClick={() => setView('agenda')}
              className={`px-4 py-1.5 text-sm ${view === 'agenda' ? 'bg-emerald-500 text-white' : 'bg-white text-slate-600'}`}
              data-testid="view-agenda-btn"
            >Agenda</button>
            <button
              type="button"
              onClick={() => setView('calendar')}
              className={`px-4 py-1.5 text-sm ${view === 'calendar' ? 'bg-emerald-500 text-white' : 'bg-white text-slate-600'}`}
              data-testid="view-calendar-btn"
            >Calendar</button>
          </div>
          <Button variant="outline" onClick={enableNotifications} data-testid="enable-notifications-btn">
            <Bell className="w-4 h-4 mr-2" /> Enable notifications
          </Button>
          <Button onClick={openScheduler} className="bg-emerald-500 hover:bg-emerald-600" data-testid="schedule-meeting-btn">
            <Plus className="w-4 h-4 mr-2" /> Schedule meeting
          </Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {view === 'agenda' && (
          <motion.div key="agenda" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
            <section>
              <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">Upcoming ({upcoming.length})</h2>
              {loading ? (
                <p className="text-slate-400">Loading…</p>
              ) : upcoming.length === 0 ? (
                <p className="text-slate-400 py-4">No upcoming meetings. Click <em>Schedule meeting</em> to create one.</p>
              ) : (
                <div className="space-y-3" data-testid="upcoming-meetings-list">
                  {upcoming.map((m) => (
                    <MeetingCard
                      key={m.id}
                      meeting={m}
                      isHost={m.host_id === user?.id}
                      onJoin={joinMeeting}
                      onJoinBrowser={joinMeetingInBrowser}
                      onCopy={copyLink}
                      onCancel={cancelMeeting}
                      onStartRec={startRecording}
                      onStopRec={stopRecording}
                      onDownloadRec={downloadRecording}
                      isRecording={recordingMeetingId === m.id}
                      uploadingRec={uploadingRec}
                    />
                  ))}
                </div>
              )}
            </section>

            {past.length > 0 && (
              <section>
                <h2 className="text-sm uppercase tracking-wide text-slate-500 mb-2">Past ({past.length})</h2>
                <div className="space-y-3 opacity-70">
                  {past.slice(0, 8).map((m) => (
                    <MeetingCard
                      key={m.id}
                      meeting={m}
                      isHost={m.host_id === user?.id}
                      onJoin={joinMeeting}
                      onJoinBrowser={joinMeetingInBrowser}
                      onCopy={copyLink}
                      onCancel={cancelMeeting}
                      onStartRec={startRecording}
                      onStopRec={stopRecording}
                      onDownloadRec={downloadRecording}
                      isRecording={recordingMeetingId === m.id}
                      uploadingRec={uploadingRec}
                    />
                  ))}
                </div>
              </section>
            )}
          </motion.div>
        )}

        {view === 'calendar' && (
          <motion.div key="calendar" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <Button variant="ghost" size="icon" onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))} data-testid="prev-month-btn">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <h3 className="text-lg font-fredoka font-bold" data-testid="current-month-label">
                    {monthAnchor.toLocaleString('default', { month: 'long', year: 'numeric' })}
                  </h3>
                  <Button variant="ghost" size="icon" onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))} data-testid="next-month-btn">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
                <div className="grid grid-cols-7 text-xs font-semibold text-slate-500 uppercase text-center mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                  {daysInMonth.map((day, idx) => {
                    if (!day) return <div key={`e-${idx}`} className="aspect-square" />;
                    const events = meetingsByDay.get(day.toDateString()) || [];
                    const isToday = isSameDay(day, new Date());
                    const isSelected = isSameDay(day, selectedDay);
                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        onClick={() => setSelectedDay(day)}
                        className={`aspect-square rounded-lg border text-sm p-1 flex flex-col items-center justify-start relative transition ${
                          isSelected ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' :
                            isToday ? 'border-emerald-300 bg-white' : 'border-slate-200 hover:border-emerald-300 bg-white'
                        }`}
                        data-testid={`calendar-day-${day.getDate()}`}
                      >
                        <span className={`text-xs font-semibold ${isToday ? 'text-emerald-600' : 'text-slate-700'}`}>
                          {day.getDate()}
                        </span>
                        {events.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-0.5 justify-center">
                            {events.slice(0, 3).map((e, i) => (
                              <span key={i} className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <h3 className="text-sm uppercase tracking-wide text-slate-500">
                {selectedDay.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
              {dayMeetings.length === 0 ? (
                <p className="text-slate-400 text-sm">No meetings on this day.</p>
              ) : (
                dayMeetings.map((m) => (
                  <MeetingCard
                    key={m.id}
                    meeting={m}
                    isHost={m.host_id === user?.id}
                    onJoin={joinMeeting}
                    onJoinBrowser={joinMeetingInBrowser}
                    onCopy={copyLink}
                    onCancel={cancelMeeting}
                    onStartRec={startRecording}
                    onStopRec={stopRecording}
                    onDownloadRec={downloadRecording}
                    isRecording={recordingMeetingId === m.id}
                    uploadingRec={uploadingRec}
                  />
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="schedule-meeting-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-emerald-500" /> Schedule a meeting
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Title</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Weekly standup"
                data-testid="meeting-title-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Description</label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Agenda / notes"
                rows={2}
                data-testid="meeting-description-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-600 font-medium">Date</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="meeting-date-input" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Time</label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="meeting-time-input" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Duration (min)</label>
                <Input
                  type="number"
                  min={5}
                  max={480}
                  value={form.duration_min}
                  onChange={(e) => setForm({ ...form, duration_min: e.target.value })}
                  data-testid="meeting-duration-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Type</label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, call_type: 'video' })}
                  className={`flex-1 rounded-lg border p-2 text-sm flex items-center justify-center gap-2 ${form.call_type === 'video' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}
                  data-testid="call-type-video"
                >
                  <Video className="w-4 h-4" /> Video
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, call_type: 'audio' })}
                  className={`flex-1 rounded-lg border p-2 text-sm flex items-center justify-center gap-2 ${form.call_type === 'audio' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}
                  data-testid="call-type-audio"
                >
                  <Phone className="w-4 h-4" /> Audio
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Attendees ({form.attendee_ids.length})</label>
              <EmployeePicker
                options={employees.filter((e) => e.user_id !== user?.id)}
                selected={form.attendee_ids}
                onChange={(ids) => setForm({ ...form, attendee_ids: ids })}
              />
            </div>

            <div className="border-t pt-3 space-y-2">
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1">
                <Shield className="w-3 h-3" /> Meeting Settings
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.waiting_room}
                    onChange={(e) => setForm({ ...form, waiting_room: e.target.checked })}
                    data-testid="setting-waiting-room"
                  />
                  Enable waiting room
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.mute_on_join}
                    onChange={(e) => setForm({ ...form, mute_on_join: e.target.checked })}
                    data-testid="setting-mute-join"
                  />
                  Mute participants on join
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.video_off_on_join}
                    onChange={(e) => setForm({ ...form, video_off_on_join: e.target.checked })}
                    data-testid="setting-video-off"
                  />
                  Video off on join
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.auto_record}
                    onChange={(e) => setForm({ ...form, auto_record: e.target.checked })}
                    data-testid="setting-auto-record"
                  />
                  Suggest auto-record
                </label>
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Password (optional)</label>
                <Input
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Leave empty for no password"
                  data-testid="meeting-password-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={createMeeting} disabled={creating} className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-meeting-submit">
              {creating ? 'Scheduling…' : (<><ExternalLink className="w-4 h-4 mr-1" /> Schedule</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
